import { nanoid } from 'nanoid';
import produce from 'immer';
import { flagToBool } from 'pano-utils';
import {
  type Hotspot,
  HSIconType,
  HSPosType,
  type KrpPlayer,
  type PolygonHotspotPoint,
} from '@types';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import { getMediaUrl, getSysUrl } from '@utils/urlMakers';
import { MainPlayer } from '@stores/krpPlayerStores';
import { IMAGE_PLACEHOLDER, VIDEO_PLACEHOLDER } from '../constants';
import { modifyHotspotTItle } from '../utils/getHotspotTItle';

function krpAddHotspot(data: Hotspot, krpPlayer?: KrpPlayer) {
  const { id, icon, zOrder, visible = 1, locked } = data;
  const player = krpPlayer || MainPlayer.get();
  if (!player) return;

  function addTitleForHotspot(): string {
    const textId = `ht_${nanoid()}`;
    const titleHotspot = modifyHotspotTItle({ ...data, id: textId });
    player?.addDivHotspot(titleHotspot);
    return textId;
  }

  function addOnClickId(ids: string[]) {
    ids.forEach((_id) => {
      player?.getHotspotById(_id).addExtendData('onClickId', id);
    });
  }

  if (icon.type === HSIconType.system) {
    // 兼容1.0传2.0的动态热点
    const isLegacySysHotspot = icon.url?.includes('v2/hotspot/80');
    const isLegacySysV1GifHotspot =
      icon.url?.includes('_gif.png') &&
      !icon.url?.includes('new_') &&
      isLegacySysHotspot;
    const isLegacySysV2GifHotspot =
      icon.url?.includes('_gif.png') &&
      icon.url?.includes('new_') &&
      isLegacySysHotspot;
    let frameCount = icon.frame;
    if (isLegacySysV1GifHotspot) {
      frameCount = 10;
    } else if (isLegacySysV2GifHotspot) {
      frameCount = 25;
    }
    player.addImageFrameHotspot({
      id,
      url: getSysUrl(icon.url),
      visible: flagToBool(visible),
      enabled: flagToBool(visible),
      ath: icon.ath,
      atv: icon.atv,
      edge: icon.edge,
      scaleX: icon.scaleX,
      scaleY: icon.scaleY,
      distorted: !!icon.stick,
      zoom: !!icon.zoom,
      zOrder,
      dragable: locked !== 1,
      frame: {
        frameWidth:
          isLegacySysHotspot ||
          isLegacySysV1GifHotspot ||
          isLegacySysV2GifHotspot
            ? 128
            : 86,
        frameHeight:
          isLegacySysHotspot ||
          isLegacySysV1GifHotspot ||
          isLegacySysV2GifHotspot
            ? 128
            : 86,
        frameCount,
        frameDuration:
          isLegacySysV1GifHotspot || isLegacySysV2GifHotspot ? 0.03 : 0.08,
      },
    });
    const textId = addTitleForHotspot();
    player.updateImageFrameHotspot({ id, titleHotspotId: textId });
    addOnClickId([textId, id]);
    player.getHotspotById(id)?.addExtendData('selectedType', 'image');
  } else if (icon.type === HSIconType.mark) {
    player.addDivHotspot(modifyHotspotTItle(data));
    player.updateHotspot({ id: data.id, dragable: data.locked !== 1 });
    addOnClickId([id]);
    player.getHotspotById(id)?.addExtendData('selectedType', 'text');
  } else if (icon.type === HSIconType.image) {
    player.addImageHotspot({
      id,
      visible: flagToBool(visible),
      enabled: flagToBool(visible),
      urls: icon.media?.length
        ? [getMediaUrl(icon.media[0].url)]
        : [IMAGE_PLACEHOLDER],
      ath: icon.ath,
      atv: icon.atv,
      edge: icon.edge,
      scaleX: icon.scaleX,
      scaleY: icon.scaleY,
      zOrder,
      dragable: icon.positionType !== HSPosType.adv && locked !== 1,
      distorted: !!icon.stick,
      zoom: !!icon.zoom,
      ...(icon.positionType === HSPosType.normal && {
        rx: icon.rx,
        ry: icon.ry,
        rz: icon.rz,
      }),
      ...(icon.positionType === HSPosType.adv && {
        hFov: icon.hFov,
        yaw: icon.yaw,
        pitch: icon.pitch,
        roll: icon.roll,
      }),
      chromakey: icon.media?.length ? icon.chromakey : '',
    });
    const textId = addTitleForHotspot();
    player.updateImageHotspot({ id, titleHotspotId: textId });
    addOnClickId([textId, id]);
    player.getHotspotById(id)?.addExtendData('selectedType', 'image');
  } else if (icon.type === HSIconType.seqFrame) {
    const media = icon.media?.[0];
    const url = media?.url ? getMediaUrl(media.url) : IMAGE_PLACEHOLDER;
    player.addImageFrameHotspot({
      id,
      visible: flagToBool(visible),
      enabled: flagToBool(visible),
      url,
      ath: icon.ath,
      atv: icon.atv,
      edge: icon.edge,
      scaleX: icon.scaleX,
      scaleY: icon.scaleY,
      zOrder,
      dragable: icon.positionType !== HSPosType.adv && locked !== 1,
      distorted: !!icon.stick,
      zoom: !!icon.zoom,
      frame: media
        ? {
            frameWidth: media.width,
            frameHeight: media.height / media.frame,
            frameCount: media.frame,
            frameDuration: media.duration,
          }
        : {
            frameWidth: 204,
            frameHeight: 204,
            frameCount: 1,
            frameDuration: 3,
          },
      ...(icon.positionType === HSPosType.normal && {
        rx: icon.rx,
        ry: icon.ry,
        rz: icon.rz,
      }),
      ...(icon.positionType === HSPosType.adv && {
        hFov: icon.hFov,
        yaw: icon.yaw,
        pitch: icon.pitch,
        roll: icon.roll,
      }),
      chromakey: media?.url ? icon.chromakey : '',
    });
    const textId = addTitleForHotspot();
    player.updateImageFrameHotspot({ id, titleHotspotId: textId });
    addOnClickId([textId, id]);
    player.getHotspotById(id)?.addExtendData('selectedType', 'image');
  } else if (icon.type === HSIconType.video) {
    const media = icon.media?.[0];
    if (media) {
      player.addVideoHotspot({
        id,
        visible: flagToBool(visible),
        enabled: flagToBool(visible),
        videoUrl: getMediaUrl(media.url),
        posterUrl: getMediaUrl(media.coverUrl),
        ath: icon.ath,
        atv: icon.atv,
        edge: icon.edge,
        scaleX: icon.scaleX,
        scaleY: icon.scaleY,
        zOrder,
        dragable: icon.positionType !== HSPosType.adv && locked !== 1,
        distorted: !!icon.stick,
        zoom: !!icon.zoom,
        pausedOnStart: true,
        ...(icon.positionType === HSPosType.normal && {
          rx: icon.rx,
          ry: icon.ry,
          rz: icon.rz,
        }),
        ...(icon.positionType === HSPosType.adv && {
          hFov: icon.hFov,
          yaw: icon.yaw,
          pitch: icon.pitch,
          roll: icon.roll,
        }),
        chromakey: icon.chromakey,
      });
    } else {
      player.addImageHotspot({
        id,
        visible: flagToBool(visible),
        enabled: flagToBool(visible),
        urls: [VIDEO_PLACEHOLDER],
        ath: icon.ath,
        atv: icon.atv,
        edge: icon.edge,
        scaleX: icon.scaleX,
        scaleY: icon.scaleY,
        zOrder,
        dragable: icon.positionType !== HSPosType.adv && locked !== 1,
        ...(icon.positionType === HSPosType.normal && {
          rx: icon.rx,
          ry: icon.ry,
          rz: icon.rz,
        }),
        ...(icon.positionType === HSPosType.adv && {
          hFov: icon.hFov,
          yaw: icon.yaw,
          pitch: icon.pitch,
          roll: icon.roll,
        }),
      });
    }
    const textId = addTitleForHotspot();
    player.updateImageFrameHotspot({ id, titleHotspotId: textId });
    addOnClickId([textId, id]);
    player.getHotspotById(id)?.addExtendData('selectedType', 'image');
  } else if (icon.type === HSIconType.polygon) {
    const fillC = colorStringToHexAndAlpha(icon.fillColor);
    const borderC = colorStringToHexAndAlpha(icon.borderColor);
    const hFillC = colorStringToHexAndAlpha(icon.overFillColor);
    const hBorderC = colorStringToHexAndAlpha(icon.overBorderColor);
    const points = produce(icon.points, (draft: PolygonHotspotPoint[]) => {
      if (draft.length) {
        draft[draft.length - 1].selected = true;
      }
    });
    player.addPolygonHotspot({
      id,
      visible: flagToBool(visible),
      enabled: flagToBool(visible),
      dragable: false,
      zOrder,
      points,
      fillColor: fillC.hex,
      fillAlpha: fillC.alpha,
      borderWidth: icon.borderWidth,
      borderColor: borderC.hex,
      borderAlpha: borderC.alpha,
      overFillColor: hFillC.hex,
      overFillAlpha: hFillC.alpha,
      overBorderColor: hBorderC.hex,
      overBorderAlpha: hBorderC.alpha,
      overBorderWidth: icon.overBorderWidth,
      blink: icon.blink === 1,
    });
    if (icon.ath !== undefined && icon.atv !== undefined) {
      const textId = `pht_${nanoid()}`;
      player.addDivHotspot(modifyHotspotTItle({ ...data, id: textId }));
      player.updateHotspot({ id: textId, dragable: locked !== 1 });
      player.getHotspotById(textId)?.addExtendData('polygonId', id);
      player.updatePolygonHotspot({ id, titleHotspotId: textId });
      addOnClickId([textId, id]);
    }
  }
}

export default krpAddHotspot;
